import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Save, UserCircle2, Volume2 } from "lucide-react";
import { toast } from "sonner";
import { api, saveAuth } from "../lib/api";
import ActiveWordListPicker from "../components/ActiveWordListPicker";

const OTHER_VALUE = "__other__";

export default function Profile() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [age, setAge] = useState("");
  const [schoolId, setSchoolId] = useState("");
  const [newSchoolName, setNewSchoolName] = useState("");
  const [grade, setGrade] = useState("");
  const [schools, setSchools] = useState([]);
  const [grades, setGrades] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pickerKey, setPickerKey] = useState(0);
  // "spell_each" | "double" — controls how the app spells repeated letters
  // aloud. Default "spell_each" matches the existing behaviour.
  const [repeatedStyle, setRepeatedStyle] = useState("spell_each");
  const [savingStyle, setSavingStyle] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [u, s, g] = await Promise.all([
          api.get("/auth/me"),
          api.get("/schools"),
          api.get("/grades"),
        ]);
        setMe(u.data);
        setAge(u.data.age || "");
        setSchoolId(u.data.school_id || "");
        setGrade(u.data.grade_level || "");
        setRepeatedStyle(u.data.repeated_letter_style === "double" ? "double" : "spell_each");
        const sorted = [...(s.data || [])].sort((a, b) =>
          (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase())
        );
        setSchools(sorted);
        setGrades(g.data || []);
      } catch (err) {
        toast.error("Could not load profile");
      }
    })();
  }, []);

  const isOther = schoolId === OTHER_VALUE;

  const onSave = async (e) => {
    e.preventDefault();
    if (isOther && !newSchoolName.trim()) {
      toast.error("Please enter your school name");
      return;
    }
    setSaving(true);
    try {
      const payload = {};
      payload.age = age ? parseInt(age, 10) : null;
      payload.grade_level = grade || "";
      if (isOther) {
        payload.new_school_name = newSchoolName.trim();
      } else {
        payload.school_id = schoolId || "";
      }
      const { data } = await api.patch("/auth/me", payload);
      // Refresh local user cache so the rest of the app sees the new role/grade
      const token = localStorage.getItem("bb_token");
      if (token) saveAuth(token, {
        id: data.id, email: data.email, name: data.name,
        role: data.role, age: data.age,
        school_id: data.school_id, grade_level: data.grade_level,
        active_wordlist_id: data.active_wordlist_id,
        repeated_letter_style: data.repeated_letter_style || "spell_each",
      });
      // After a school/grade change the active list may have been cleared
      // by the backend; remount the picker so it shows the fresh state.
      setMe(data);
      setSchoolId(data.school_id || "");
      setNewSchoolName("");
      setPickerKey((k) => k + 1);
      toast.success("Profile saved!");
      if (isOther) {
        toast.info("Your school is pending approval.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save profile");
    } finally { setSaving(false); }
  };

  if (!me) return <div className="card-soft text-center py-12 text-slate-500">Loading…</div>;

  // Persist the spelling-style preference immediately on radio change so the
  // kid never has to scroll back and click a separate Save button. We re-use
  // PATCH /auth/me + refresh the cached user object so spellOut() picks it
  // up on the next playback.
  const onChangeRepeatedStyle = async (value) => {
    if (value !== "spell_each" && value !== "double") return;
    const prev = repeatedStyle;
    setRepeatedStyle(value);
    setSavingStyle(true);
    try {
      const { data } = await api.patch("/auth/me", { repeated_letter_style: value });
      const token = localStorage.getItem("bb_token");
      if (token) saveAuth(token, {
        id: data.id, email: data.email, name: data.name,
        role: data.role, age: data.age,
        school_id: data.school_id, grade_level: data.grade_level,
        active_wordlist_id: data.active_wordlist_id,
        repeated_letter_style: data.repeated_letter_style || "spell_each",
      });
      setMe(data);
      toast.success(value === "double" ? "Now using \"Double letters\"" : "Now spelling each letter");
    } catch (err) {
      // Revert on failure so the UI matches what's actually saved.
      setRepeatedStyle(prev);
      toast.error(err?.response?.data?.detail || "Could not save preference");
    } finally {
      setSavingStyle(false);
    }
  };

  // If the user's saved school is pending it won't be in the public schools
  // list — surface that gracefully so they don't see "— none —".
  const knownSchool = !schoolId || schoolId === OTHER_VALUE || schools.some((s) => s.id === schoolId);

  return (
    <div className="max-w-xl mx-auto space-y-6" data-testid="profile-page">
      <motion.div
        initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="text-center mb-6">
          <div className="inline-flex w-20 h-20 rounded-3xl bg-[#4D96FF] items-center justify-center shadow-[0_8px_0_#1D4ED8] mb-3">
            <UserCircle2 className="w-10 h-10 text-white" strokeWidth={3} />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#1E1B4B]">Your profile</h1>
          <p className="text-slate-600 font-semibold mt-1">{me.email}</p>
        </div>

        <form onSubmit={onSave} className="card-soft space-y-4" data-testid="profile-form">
          <div>
            <label className="block text-sm font-bold text-[#1E1B4B] mb-1.5 ml-2">Name</label>
            <input
              value={me.name || ""}
              disabled
              className="w-full rounded-2xl border-4 border-blue-100 px-5 py-4 text-lg font-semibold bg-blue-50 text-slate-500"
              data-testid="profile-name"
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-[#1E1B4B] mb-1.5 ml-2">Age</label>
            <input
              type="number" min="3" max="99"
              value={age} onChange={(e) => setAge(e.target.value)}
              className="w-full rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-5 py-4 text-lg outline-none transition-all font-semibold"
              placeholder="7"
              data-testid="profile-age-input"
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-[#1E1B4B] mb-1.5 ml-2">School</label>
            <select
              value={schoolId} onChange={(e) => setSchoolId(e.target.value)}
              className="w-full rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-3 py-4 text-base outline-none transition-all font-semibold bg-white"
              data-testid="profile-school-input"
            >
              <option value="">— none —</option>
              {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              <option value={OTHER_VALUE}>Other (add a new school)</option>
            </select>
            {isOther && (
              <>
                <input
                  value={newSchoolName}
                  onChange={(e) => setNewSchoolName(e.target.value)}
                  placeholder="Type your school's name"
                  className="mt-2 w-full rounded-2xl border-4 border-amber-200 focus:border-[#FBBF24] px-5 py-3 text-base outline-none transition-all font-semibold"
                  data-testid="profile-new-school-input"
                  maxLength={80}
                />
                <p className="text-xs text-amber-700 mt-1 ml-2 font-semibold" data-testid="profile-pending-school-note">
                  Your school will be added as <span className="underline">pending</span> until an admin approves it.
                </p>
              </>
            )}
            {!knownSchool && schoolId && (
              <p className="text-xs text-amber-700 mt-1 ml-2 font-semibold">
                Your current school is pending approval.
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-bold text-[#1E1B4B] mb-1.5 ml-2">Grade</label>
            <select
              value={grade} onChange={(e) => setGrade(e.target.value)}
              className="w-full rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-3 py-4 text-base outline-none transition-all font-semibold bg-white"
              data-testid="profile-grade-input"
            >
              <option value="">— pick later —</option>
              {grades.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <button type="submit" disabled={saving} className="btn-clay-blue w-full" data-testid="profile-save-btn">
            <Save className="w-5 h-5" strokeWidth={3} /> {saving ? "Saving…" : "Save profile"}
          </button>
        </form>
      </motion.div>

      <section data-testid="profile-spelling-style">
        <h2 className="font-display text-xl font-bold text-[#1E1B4B] mb-3 ml-1">How should we spell repeated letters?</h2>
        <div className="card-soft space-y-3">
          <p className="text-sm font-semibold text-slate-600 ml-1">
            Pick how the app says back-to-back identical letters when it spells a word out loud.
          </p>
          <div className="space-y-2.5">
            {[
              {
                value: "spell_each",
                title: "Spell each letter",
                example: "apple → A, P, P, L, E",
                testId: "spelling-style-spell-each",
              },
              {
                value: "double",
                title: "Use double letters",
                example: "apple → A, Double P, L, E",
                testId: "spelling-style-double",
              },
            ].map((opt) => {
              const active = repeatedStyle === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => onChangeRepeatedStyle(opt.value)}
                  disabled={savingStyle}
                  data-testid={opt.testId}
                  aria-pressed={active}
                  className={`w-full text-left rounded-2xl border-4 px-4 py-3 transition-all disabled:opacity-60 ${
                    active
                      ? "border-[#4D96FF] bg-blue-50 shadow-[0_4px_0_#1D4ED8]"
                      : "border-blue-100 bg-white hover:border-blue-300"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-6 h-6 rounded-full border-4 flex items-center justify-center shrink-0 ${
                        active ? "border-[#4D96FF] bg-[#4D96FF]" : "border-blue-200 bg-white"
                      }`}
                      aria-hidden="true"
                    >
                      {active && <div className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Volume2 className="w-4 h-4 text-[#4D96FF]" strokeWidth={3} />
                        <span className="font-display text-lg font-bold text-[#1E1B4B]">{opt.title}</span>
                        {opt.value === "spell_each" && (
                          <span className="text-[10px] font-bold uppercase tracking-widest bg-blue-100 text-[#1D4ED8] px-2 py-0.5 rounded-full">
                            Default
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 font-semibold mt-0.5">{opt.example}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          <p className="text-xs text-slate-500 ml-1">
            Only affects letter-by-letter spelling in Practice (Hear letters) and Listen mode. Full-word pronunciations are unchanged.
          </p>
        </div>
      </section>

      <section data-testid="profile-active-picker">
        <h2 className="font-display text-xl font-bold text-[#1E1B4B] mb-3 ml-1">Your active word list</h2>
        <ActiveWordListPicker key={pickerKey} />
      </section>
    </div>
  );
}
